import React, { useState } from "react";
import { Modal, Form, Select, Checkbox, Button, Empty, Tabs } from "antd";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaClinicMedical, FaUserMd } from "react-icons/fa";

const SharedForm = ({ form, onFinish, isSubmitting, onClose, clinics, doctors, showDoctor, handleAddFirstClinic, activeTab }) => {
    const dayOptions = [
        { value: 0, label: "Sunday" },
        { value: 1, label: "Monday" },
        { value: 2, label: "Tuesday" },
        { value: 3, label: "Wednesday" },
        { value: 4, label: "Thursday" },
        { value: 5, label: "Friday" },
        { value: 6, label: "Saturday" },
    ];

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ clinicId: undefined, doctorId: undefined, dayOfWeek: 0, isWorking: true }}
        >
            <Form.Item
                name="clinicId"
                label="Clinic"
                rules={[{ required: true, message: "Please select a clinic" }]}
            >
                <Select
                    placeholder="Select a clinic"
                    dropdownRender={(menu) => (
                        <div>
                            {menu}
                            <div className="border-t p-2">
                                <Button
                                    type="text"
                                    icon={<FaPlus />}
                                    onClick={handleAddFirstClinic}
                                    className="w-full text-primary text-left flex items-center"
                                    size="small"
                                >
                                    Add New Clinic
                                </Button>
                            </div>
                        </div>
                    )}
                >
                    {clinics.map((clinic) => (
                        <Select.Option key={clinic.clinicId} value={clinic.clinicId}>
                            {clinic.clinicName}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {showDoctor && (
                <Form.Item
                    name="doctorId"
                    label="Consultant"
                    rules={[{ required: true, message: "Please select a Consultant" }]}
                >
                    <Select placeholder="Select a Consultant">
                        {doctors.map((doctor) => (
                            <Select.Option key={doctor.doctorId} value={doctor.doctorId}>
                                {doctor.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            <Form.Item
                name="dayOfWeek"
                label="Day"
                rules={[{ required: true, message: "Please select a day" }]}
            >
                <Select placeholder="Select a day">
                    {dayOptions.map((day) => (
                        <Select.Option key={day.value} value={day.value}>
                            {day.label}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="isWorking" valuePropName="checked" noStyle>
                <Checkbox>Active</Checkbox>
            </Form.Item>

            <div className="flex justify-end gap-2 mt-6">
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    size="middle"
                    className="bg-primary text-white border-0 font-semibold transition-all"
                    htmlType="submit"
                    loading={isSubmitting}
                >
                    {isSubmitting ? "Adding..." : "Add Working Day"}
                </Button>
            </div>
        </Form>
    );
};

const AddWorkingDayModal = ({ isOpen, onClose, onSubmit, isSubmitting, clinics, doctors }) => {
    const [generalForm] = Form.useForm();
    const [doctorForm] = Form.useForm();
    const [activeTab, setActiveTab] = useState("general");
    const navigate = useNavigate();

    const hasNoClinics = !clinics || clinics.length === 0;
    const hasNoDoctors = !doctors || doctors.length === 0;

    const handleAddFirstClinic = () => {
        onClose();
        navigate("/clinics");
    };

    const handleSubmit = async (data) => {
        try {
            await onSubmit({ ...data, type: activeTab });
            generalForm.resetFields();
            doctorForm.resetFields();
        } catch (error) {}
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        generalForm.resetFields();
        doctorForm.resetFields();
    };

    const EmptyDoctors = () => (
        <div className="text-center py-8">
            <Empty
                image={<FaUserMd className="text-4xl text-gray-400 mx-auto mb-4" />}
                description={
                    <div>
                        <p className="text-gray-500 mb-2">No Consultants available</p>
                        <p className="text-sm text-gray-400">
                            You need to have Consultants in your center before configuring working days
                        </p>
                    </div>
                }
            />
            <div className="flex justify-center gap-2 mt-6">
                <Button onClick={onClose}>Cancel</Button>
            </div>
        </div>
    );

    return (
        <Modal
            title="Add Working Day"
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={600}
            className="animate-fade-in"
        >
            <p className="text-gray-500 mb-4">
                Configure a new working day for a clinic and Consultant
            </p>

            {hasNoClinics ? (
                <div className="text-center py-8">
                    <Empty
                        image={<FaClinicMedical className="text-4xl text-gray-400 mx-auto mb-4" />}
                        description={
                            <div>
                                <p className="text-gray-500 mb-2">No clinics available</p>
                                <p className="text-sm text-gray-400">
                                    You need to add a clinic first before configuring working days
                                </p>
                            </div>
                        }
                    />
                    <div className="flex justify-center gap-2 mt-6">
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            icon={<FaPlus />}
                            onClick={handleAddFirstClinic}
                            className="flex items-center"
                        >
                            Add Your First Clinic
                        </Button>
                    </div>
                </div>
            ) : (
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={[
                        {
                            key: "general",
                            label: (
                                <span className="flex items-center gap-2">
                                    <FaClinicMedical />
                                    General Day
                                </span>
                            ),
                            children: (
                                <SharedForm
                                    form={generalForm}
                                    onFinish={handleSubmit}
                                    isSubmitting={isSubmitting}
                                    onClose={onClose}
                                    clinics={clinics}
                                    doctors={doctors}
                                    showDoctor={false}
                                    handleAddFirstClinic={handleAddFirstClinic}
                                    activeTab={activeTab}
                                />
                            ),
                        },
                        {
                            key: "doctor",
                            label: (
                                <span className="flex items-center gap-2">
                                    <FaUserMd />
                                    Consultant
                                </span>
                            ),
                            children: hasNoDoctors ? (
                                <EmptyDoctors />
                            ) : (
                                <SharedForm
                                    form={doctorForm}
                                    onFinish={handleSubmit}
                                    isSubmitting={isSubmitting}
                                    onClose={onClose}
                                    clinics={clinics}
                                    doctors={doctors}
                                    showDoctor={true}
                                    handleAddFirstClinic={handleAddFirstClinic}
                                    activeTab={activeTab}
                                />
                            ),
                        },
                    ]}
                />
            )}
        </Modal>
    );
};

export default AddWorkingDayModal;